import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { trackEvent } from "~/lib/analytics";
import { logger } from "~/lib/logger";
import { useAccountResearchRelease } from "~/lib/user";
import type { SessionWithParts } from "~/types";

import { useApiClient } from "./use-api-client";
import { useSession } from "./use-session";
interface UseSessionResearchReleaseProps {
    sessionId: string;
}

export const useSessionResearchRelease = ({
    sessionId,
}: UseSessionResearchReleaseProps) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const { sessionToken } = useSession(sessionId);

    // AIDEV-NOTE: Query to get session's research release status
    // Data is prefetched on server in _protected.sessions.$sessionId._index.tsx
    // The queryFn is provided as a fallback when SSR prefetch/hydration fails.
    // It makes a direct fetch WITHOUT the Authorization header to avoid Supabase client inconsistencies.
    const { data: sessionResearchReleaseResponse, isLoading } = useQuery<{
        hasResearchRelease: boolean | null;
        researchReleaseInherited: boolean;
    }>({
        queryKey: ["sessionResearchRelease", sessionId],
        queryFn: async () => {
            // AIDEV-NOTE: Use native fetch WITHOUT Authorization header to avoid Supabase SSR client issues.
            // The apiClient adds Authorization header which causes inconsistent results.
            const response = await fetch(
                `/api/session/${sessionId}/research-release`,
                {
                    method: "GET",
                    credentials: "include", // Include cookies for auth
                    headers: {
                        "Content-Type": "application/json",
                        // No Authorization header - rely on cookies only
                    },
                },
            );
            if (!response.ok) {
                throw new Error("Failed to fetch session research release");
            }
            return response.json();
        },
        enabled: !!sessionId && !sessionToken,
        // AIDEV-NOTE: staleTime: 0 + refetchOnMount ensures queryFn is always called on mount.
        // SSR hydration doesn't work reliably, so we always fetch client-side.
        staleTime: 0,
        gcTime: 5 * 60 * 1000, // 5 minutes
        refetchOnMount: true, // Trigger queryFn when component mounts
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const {
        hasResearchRelease: sessionHasResearchRelease,
        researchReleaseInherited,
    } = sessionResearchReleaseResponse ?? {
        hasResearchRelease: undefined,
        researchReleaseInherited: true,
    };

    // Query to get user's research release preference
    const {
        hasResearchRelease: userHasResearchRelease,
        isLoading: isUserLoading,
    } = useAccountResearchRelease();

    // AIDEV-NOTE: Derive effective value so UI doesn't regress when a single query response
    // briefly reports false while inheritance should come from the user's setting (EVO-761)
    const effectiveResearchReleaseEnabled =
        researchReleaseInherited && userHasResearchRelease !== undefined
            ? userHasResearchRelease
            : sessionHasResearchRelease ?? userHasResearchRelease;

    // Mutation to toggle research release for a session
    const { mutate: toggleResearchRelease, isPending } = useMutation({
        mutationFn: async (hasResearchRelease: boolean) => {
            if (!sessionId) {
                throw new Error("Session ID is required");
            }

            logger
                .withMetadata({ sessionId, hasResearchRelease })
                .info("Toggling session donation status");

            const response = await apiClient.put(
                `/session/${sessionId}/research-release`,
                {
                    hasResearchRelease,
                },
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    error.error || "Failed to update donation status",
                );
            }

            return response.json();
        },
        onMutate: async (hasResearchRelease) => {
            if (!sessionId) {
                throw new Error("Session ID is required");
            }

            // Cancel any outgoing refetches for queries we're optimistically updating
            // AIDEV-NOTE: Don't cancel sessionResearchRelease - we need it to refetch in onSuccess
            await queryClient.cancelQueries({
                queryKey: ["sessionStatus", sessionId],
            });
            await queryClient.cancelQueries({
                queryKey: ["session", sessionId],
            });

            // Snapshot the previous values
            const previousSessionStatus = queryClient.getQueryData([
                "sessionStatus",
                sessionId,
            ]);
            const previousSession = queryClient.getQueryData([
                "session",
                sessionId,
            ]) as SessionWithParts | null;
            const previousResearchRelease = queryClient.getQueryData([
                "sessionResearchRelease",
                sessionId,
            ]);

            // Optimistically update the session status
            queryClient.setQueryData(
                ["sessionStatus", sessionId],
                (old: Record<string, unknown> | undefined) => {
                    if (!old) return old;
                    return {
                        ...old,
                        hasResearchRelease,
                    };
                },
            );

            // Optimistically update the full session data
            queryClient.setQueryData(
                ["session", sessionId],
                (old: SessionWithParts | null) => {
                    if (!old) return old;
                    return {
                        ...old,
                        session: {
                            ...old.session,
                            hasResearchRelease,
                        },
                    };
                },
            );

            // Optimistically update the research release status
            // AIDEV-NOTE: Preserve object structure for proper UI updates (fixes EVO-761)
            queryClient.setQueryData(
                ["sessionResearchRelease", sessionId],
                (
                    old:
                        | {
                              hasResearchRelease: boolean | null;
                              researchReleaseInherited: boolean;
                          }
                        | undefined,
                ) => ({
                    ...old,
                    hasResearchRelease,
                    researchReleaseInherited: false, // When explicitly set, it's no longer inherited
                }),
            );

            return {
                previousSessionStatus,
                previousSession,
                previousResearchRelease,
            };
        },
        onError: (error, hasResearchRelease, context) => {
            // Log the error
            logger
                .withError(error)
                .withMetadata({ sessionId, hasResearchRelease })
                .error("Failed to toggle donation status");

            // Show error toast
            if (error.message.includes("must enable session donation")) {
                toast.error(
                    "You must enable session donation in your account settings first",
                );
            } else {
                toast.error("Failed to update session donation status");
            }

            // Revert the optimistic updates
            if (context) {
                queryClient.setQueryData(
                    ["sessionStatus", sessionId],
                    context.previousSessionStatus,
                );
                queryClient.setQueryData(
                    ["session", sessionId],
                    context.previousSession,
                );
                queryClient.setQueryData(
                    ["sessionResearchRelease", sessionId],
                    context.previousResearchRelease,
                );
            }
        },
        onSuccess: async (data, hasResearchRelease) => {
            // Show success toast
            toast.success(
                hasResearchRelease
                    ? `This session is now shared with the team. Thank you!`
                    : `This session is no longer shared with the team.`,
            );

            trackEvent("changed session donation preference", {
                preference: hasResearchRelease ? "share" : "private",
            });

            // AIDEV-NOTE: Only refetch session and sessionStatus which use server-side data fetching.
            // Do NOT refetch sessionResearchRelease - it doesn't make API calls anymore (see queryFn above).
            // The optimistic update in onMutate already set the correct value for sessionResearchRelease.
            await Promise.all([
                queryClient.refetchQueries({
                    queryKey: ["session", sessionId],
                }),
                queryClient.refetchQueries({
                    queryKey: ["sessionStatus", sessionId],
                }),
            ]);
        },
    });

    // AIDEV-NOTE: Wrap toggleResearchRelease to prevent unwanted calls from Radix components.
    // Some Radix UI controls (Select, Checkbox) may fire onValueChange/onCheckedChange
    // during render/hydration with stale values. This guard ensures we only toggle
    // when the value actually differs from current state.
    const safeToggleResearchRelease = (newValue: boolean) => {
        if (newValue === sessionHasResearchRelease) {
            return;
        }
        toggleResearchRelease(newValue);
    };

    return {
        sessionHasResearchReleaseEnabled: sessionHasResearchRelease,
        userHasResearchReleaseEnabled: userHasResearchRelease,
        effectiveResearchReleaseEnabled,
        researchReleaseInherited,
        toggleResearchRelease: safeToggleResearchRelease,
        isPending,
        isLoading: isLoading || isUserLoading,
    };
};
